import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const TYPE_LABELS = {
  clock_in:    { label: 'Zameldowano',        color: '#27AE60', icon: '✓' },
  clock_out:   { label: 'Wymeldowano',        color: '#A32D2D', icon: '✗' },
  break_start: { label: 'Przerwa rozpoczęta', color: '#EF9F27', icon: '⏸' },
  break_end:   { label: 'Przerwa zakończona', color: '#185FA5', icon: '▶' },
};

function formatTime(str) {
  if (!str) return '';
  return new Date(str).toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' });
}

export default function Attendance() {
  const { user, terminalToken } = useAuth();
  const [last, setLast]       = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [toast, setToast]     = useState('');
  const token = (window.__cafeApiToken || localStorage.getItem('cafepos_token'));

  function loadHistory() {
    if (!user?.id) return;
    fetch(`/api/attendance/history/${user.id}?limit=200`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(setHistory)
      .catch(() => {});
  }

  useEffect(() => {
    if (!user?.id) return;
    fetch(`/api/attendance/status/${user.id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.json())
      .then(d => setLast(d.last))
      .catch(() => {});
    loadHistory();
  }, [user?.id, token]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 3000); }

  async function register(type) {
    setLoading(true);
    try {
      const res = await fetch('/api/attendance', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ type, user_id: user.id }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setLast(data.last);
      showToast(TYPE_LABELS[type]?.label || 'Zapisano');
      loadHistory();
    } catch (err) {
      showToast('Błąd: ' + err.message);
    } finally {
      setLoading(false);
    }
  }

  const lastType  = last?.type;
  const isAtWork  = lastType === 'clock_in' || lastType === 'break_end';
  const isOnBreak = lastType === 'break_start';
  const isOut     = !lastType || lastType === 'clock_out';

  return (
    <div style={{ padding: 24, display: 'flex', gap: 24, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ maxWidth: 480, flex: '1 1 380px' }}>
        <h2 style={{ fontSize: 18, fontWeight: 500, marginBottom: 4 }}>Czas pracy</h2>
        <p style={{ fontSize: 13, color: 'var(--text-muted)', marginBottom: 20 }}>{user?.name}</p>

        {last && (
          <div style={{
            background: 'var(--surface)', border: '0.5px solid var(--border)',
            borderRadius: 10, padding: '14px 16px', marginBottom: 20,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{ fontSize: 24 }}>{TYPE_LABELS[last.type]?.icon}</span>
            <div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)' }}>Ostatnie zdarzenie</div>
              <div style={{ fontSize: 15, fontWeight: 500, color: TYPE_LABELS[last.type]?.color }}>
                {TYPE_LABELS[last.type]?.label}
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {formatTime(last.created_at)}
              </div>
            </div>
          </div>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <button onClick={() => register('clock_in')} disabled={loading || isAtWork || isOnBreak}
            style={{
              padding: '16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontSize: 16, fontWeight: 600,
              background: isOut ? '#27AE60' : 'var(--surface)',
              color: isOut ? '#fff' : 'var(--text-muted)',
              opacity: (isAtWork || isOnBreak) ? 0.4 : 1,
            }}>
            ✓ Zamelduj
          </button>

          <div style={{ display: 'flex', gap: 10 }}>
            <button onClick={() => register('break_start')} disabled={loading || !isAtWork}
              style={{
                flex: 1, padding: '16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                background: isAtWork ? '#EF9F27' : 'var(--surface)',
                color: isAtWork ? '#fff' : 'var(--text-muted)',
                opacity: !isAtWork ? 0.4 : 1,
              }}>
              ⏸ Rozpocznij przerwę
            </button>
            <button onClick={() => register('break_end')} disabled={loading || !isOnBreak}
              style={{
                flex: 1, padding: '16px', borderRadius: 10, border: 'none', cursor: 'pointer',
                fontSize: 14, fontWeight: 500,
                background: isOnBreak ? '#185FA5' : 'var(--surface)',
                color: isOnBreak ? '#fff' : 'var(--text-muted)',
                opacity: !isOnBreak ? 0.4 : 1,
              }}>
              ▶ Zakończ przerwę
            </button>
          </div>

          <button onClick={() => register('clock_out')} disabled={loading || isOut}
            style={{
              padding: '16px', borderRadius: 10, border: 'none', cursor: 'pointer',
              fontSize: 16, fontWeight: 600,
              background: !isOut ? '#A32D2D' : 'var(--surface)',
              color: !isOut ? '#fff' : 'var(--text-muted)',
              opacity: isOut ? 0.4 : 1,
            }}>
            ✗ Wymelduj
          </button>
        </div>
      </div>

      <div style={{ flex: '1 1 320px', maxWidth: 420 }}>
        <h3 style={{ fontSize: 15, fontWeight: 500, marginBottom: 12, marginTop: 2 }}>Historia zdarzeń</h3>
        <div style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, overflowY: 'auto', maxHeight: 'calc(100vh - 140px)' }}>
          {history.length === 0 ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>Brak zdarzeń</div>
          ) : (
            Object.entries(groupByDay(history)).map(([day, events], gi, arr) => {
              const workedMs = calcWorkedMs(events);
              return (
              <div key={day}>
                <div style={{
                  padding: '8px 16px', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)',
                  textTransform: 'uppercase', letterSpacing: '.04em',
                  background: 'var(--bg)', borderBottom: '0.5px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                }}>
                  <span>{day}</span>
                  {workedMs > 0 && (
                    <span style={{ color: 'var(--text)', fontWeight: 700, letterSpacing: 0, textTransform: 'none' }}>
                      {formatDuration(workedMs)}
                    </span>
                  )}
                </div>
                {events.map((ev, i) => (
                  <div key={ev.id} style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 16px',
                    borderBottom: (i < events.length - 1 || gi < arr.length - 1) ? '0.5px solid var(--border)' : 'none',
                  }}>
                    <span style={{ fontSize: 16 }}>{TYPE_LABELS[ev.type]?.icon}</span>
                    <span style={{ flex: 1, fontSize: 13, color: TYPE_LABELS[ev.type]?.color, fontWeight: 500 }}>
                      {TYPE_LABELS[ev.type]?.label}
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{formatTime(ev.created_at)}</span>
                  </div>
                ))}
              </div>
              );
            })
          )}
        </div>
      </div>

      {toast && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          background: '#222', color: '#fff', padding: '10px 20px', borderRadius: 8,
          fontSize: 14, fontWeight: 500, zIndex: 999,
        }}>{toast}</div>
      )}
    </div>
  );
}

function groupByDay(events) {
  const groups = {};
  const order = [];
  events.forEach(ev => {
    const d = new Date(ev.created_at);
    const key = d.toLocaleDateString('pl-PL', { weekday: 'long', day: '2-digit', month: '2-digit', year: 'numeric' });
    if (!groups[key]) { groups[key] = []; order.push(key); }
    groups[key].push(ev);
  });
  // events są malejąco po czasie, więc kolejność dni w `order` jest już od najnowszego — bierzemy max 30
  const limitedKeys = order.slice(0, 30);
  const limited = {};
  limitedKeys.forEach(k => { limited[k] = groups[k]; });
  return limited;
}

// Liczy łączny czas pracy w danym dniu na podstawie par clock_in/clock_out,
// odejmując przerwy (break_start/break_end). Wydarzenia w grupie są malejąco
// po czasie (najnowsze pierwsze), więc iterujemy od najstarszego.
function calcWorkedMs(dayEvents) {
  const chrono = [...dayEvents].reverse(); // najstarsze najpierw
  let workedMs = 0;
  let sessionStart = null;
  let breakStart = null;

  for (const ev of chrono) {
    const t = new Date(ev.created_at).getTime();
    if (ev.type === 'clock_in') {
      sessionStart = t;
    } else if (ev.type === 'clock_out') {
      if (sessionStart !== null) {
        workedMs += t - sessionStart;
        sessionStart = null;
      }
      breakStart = null;
    } else if (ev.type === 'break_start') {
      breakStart = t;
    } else if (ev.type === 'break_end') {
      if (breakStart !== null) {
        workedMs -= t - breakStart;
        breakStart = null;
      }
    }
  }
  return Math.max(0, workedMs);
}

function formatDuration(ms) {
  const totalMin = Math.round(ms / 60000);
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  if (h === 0) return `${m} min`;
  return `${h} godz. ${m > 0 ? m + ' min' : ''}`.trim();
}
